import { cx } from './Primitives';
import { MARK_REGULAR, MARK_SMALL, WORDMARK_DISPLAY, WORDMARK_SMALL } from './brandArt';

/**
 * The Timely Payment wordmark (DESIGN-SYSTEM §13; docs/brand/README.md).
 *
 * The name in custom-set lettering, with the settle mark — the square that stands for the dot of the i —
 * in jade, the money accent. Two cuts: `display` for headers and the site; `small` (looser, a larger
 * mark) for the sidebar and a phone's top bar, where it is 18 px tall or less.
 *
 * - Height comes from `className` (`h-5`, `h-6`…); the width follows the drawing.
 * - Colour: brand ink (navy on light, white in dark mode) by default; `onChrome` for the navy frame and the
 *   site's dark sections — white letters, the light jade mark.
 * - The drawing is hidden from assistive technology and the name is said once, as text, so a link around
 *   it reads "Timely Payment" to everybody.
 */
export const Wordmark = ({
    className, cut = 'display', onChrome = false,
}: {
    className?: string;
    cut?: 'display' | 'small';
    onChrome?: boolean;
}) => {
    const art = cut === 'small' ? WORDMARK_SMALL : WORDMARK_DISPLAY;
    return (
        <span className={cx('inline-flex items-center flex-none', onChrome ? 'text-chrome-label' : 'text-brand-ink', className ?? 'h-5')}>
            <svg viewBox={art.viewBox} className="h-full w-auto block" aria-hidden="true" focusable="false">
                <path d={art.path} fill="currentColor" />
                <rect x={art.mark.x} y={art.mark.y} width={art.mark.size} height={art.mark.size}
                    className={onChrome ? 'fill-mark-on-chrome' : 'fill-mark'} />
            </svg>
            <span className="sr-only">Timely Payment</span>
        </span>
    );
};

/**
 * The compact mark — the T and its settle mark on a navy tile — for places a whole name will not fit: an
 * app icon, an avatar-sized brand spot. `size` is in pixels; under 40 px the small cut is used, so neither
 * the T nor the square vanishes. Decorative unless given a `title`.
 */
export const BrandTile = ({ size = 40, className, title }: { size?: number; className?: string; title?: string }) => {
    const c = size <= 40 ? MARK_SMALL : MARK_REGULAR;
    const pad = size <= 40 ? 0.18 : 0.24;
    const S = Math.max(c.width, c.cap) / (1 - 2 * pad);
    const ox = (S - c.width) / 2 - c.x0;
    const oy = (S - c.cap) / 2 + c.cap;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${Math.round(S)} ${Math.round(S)}`} className={cx('flex-none block', className)}
            role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true} focusable="false">
            <rect width={Math.round(S)} height={Math.round(S)} rx={Math.round(S * 0.22)} className="fill-chrome" />
            <g transform={`translate(${Math.round(ox)} ${Math.round(oy)})`}>
                <path d={c.path} className="fill-chrome-label" />
                <rect x={c.mark.x} y={c.mark.y} width={c.mark.size} height={c.mark.size} className="fill-mark-on-chrome" />
            </g>
        </svg>
    );
};
